import InfoIcon from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useRef, useState } from "react";
import Tree from "react-d3-tree";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import CButton from "../../components/CButton";
import CCheckbox from "../../components/CCheckbox";
import CDialog from "../../components/CDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import {
  ORGANOGRAM_FIELDS,
  ORGANOGRAM_TEMPLATE_SAMPLE,
} from "../../constant/constants.js";
import {
  archiveRecords,
  downloadExcelTemplateUtility,
  getLocalData,
  isNoDepartment,
} from "../../constant/utils/commonUtils";
import { statuses } from "../../constant/constants";
// Constants
const localData = JSON.parse(localStorage.getItem("sdfUser"));

const initialRows = [
  {
    id: 1,
    departmentId: "Robert",
    departmentName: "Sales",
    departmentHeadId: "8001",
    departmentHeadName: "Soft Skills",
    parentDepartmentName: "John",
  },
  {
    id: 2,
    departmentId: "Robert",
    departmentName: "Sales",
    departmentHeadId: "8001",
    departmentHeadName: "Soft Skills",
    parentDepartmentName: "John",
  },
  {
    id: 3,
    departmentId: "Robert",
    departmentName: "Sales",
    departmentHeadId: "8001",
    departmentHeadName: "Soft Skills",
    parentDepartmentName: "John",
  },
];
// Utility Functions
const getFieldHeaderName = (fieldName) => {
  return ORGANOGRAM_FIELDS.find((f) => f.field === fieldName)?.headerName;
};

// Excel Template Functions
const downloadOrganogramTemplate = () => {
  const templateColumns = ORGANOGRAM_FIELDS;

  const fields = templateColumns.map((col) => ({
    fieldName: col.field, // for mapping data
    headerName: col.headerName, // used for headers
  }));

  const dropdownConfigs = []; // If no dropdowns, keep this empty

  const sampleRows = ORGANOGRAM_TEMPLATE_SAMPLE.map((row) =>
    ORGANOGRAM_FIELDS.map((col) => {
      const v = row[col.field];
      return v != null && v !== "" ? String(v) : "";
    }),
  );

  downloadExcelTemplateUtility({
    fileName: "Organogram_Template.xlsx",
    sheetName: "Organogram",
    columns: fields,
    sampleRows,
    dropdownConfigs,
    context: {},
    dataValidationStartRow: 2,
    dataValidationEndRow: 1000,
    getHeader: (col) => col.headerName,
  });
};

// Data Validation and Processing Functions
const validateDepartmentData = (
  validatedDepartments,
  index,
  allValidationErrors,
  seenDeptIds,
) => {
  const deptNameField = getFieldHeaderName("dept_name");
  const deptIdField = getFieldHeaderName("dept_id");
  const parentDeptIdField = getFieldHeaderName("parent_dept_id");
  const deptHeadIdField = getFieldHeaderName("dept_head_id");

  const rawDeptId = String(validatedDepartments[deptIdField] || "").trim();
  const rawDeptName = String(validatedDepartments[deptNameField] || "").trim();
  const isNullDepartmentRow = !rawDeptId && !rawDeptName;

  if (isNullDepartmentRow) {
    const blankDeptKey = "__no_dept__";
    if (seenDeptIds.has(blankDeptKey)) {
      allValidationErrors.push(
        `Row ${index + 1}: Duplicate null/blank department. Only one empty department row is allowed.`,
      );
    } else {
      seenDeptIds.add(blankDeptKey);
    }
    return {
      deptNameField,
      deptIdField,
      parentDeptIdField,
      deptHeadIdField,
    };
  }

  // 🔵 Required department name
  if (!rawDeptName) {
    allValidationErrors.push(`Row ${index + 1}: ${deptNameField} is required.`);
  }

  // 🟢 Required dept_id
  if (!rawDeptId) {
    allValidationErrors.push(`Row ${index + 1}: ${deptIdField} is required.`);
  } else if (seenDeptIds.has(rawDeptId)) {
    allValidationErrors.push(
      `Row ${index + 1}: Duplicate Department ID "${rawDeptId}" found.`,
    );
  } else {
    seenDeptIds.add(rawDeptId);
  }

  return {
    deptNameField,
    deptIdField,
    parentDeptIdField,
    deptHeadIdField,
  };
};

const validateDepartmentHead = (
  deptHeadId,
  hasEmployees,
  validEmployeeIds,
  index,
  deptHeadIdField,
  allValidationErrors,
) => {
  if (
    deptHeadId !== null &&
    deptHeadId !== undefined &&
    String(deptHeadId).trim() === ""
  ) {
    allValidationErrors.push(
      `Row ${
        index + 1
      }: ${deptHeadIdField} cannot be empty. Please provide a valid ID or leave it blank.`,
    );
  }

  if (deptHeadId && hasEmployees) {
    const deptHeadIdStr = String(deptHeadId).trim();
    const isValid = validEmployeeIds.some(
      (id) => String(id).trim() === deptHeadIdStr,
    );

    if (!isValid) {
      allValidationErrors.push(
        `Row ${
          index + 1
        }: Invalid ${deptHeadIdField} "${deptHeadId}". This employee ID does not exist in the company.`,
      );
    }
  }
};

const validateParentDepartment = (
  processedData,
  existingDeptIds,
  allValidationErrors,
) => {
  const allDeptIdsInFile = processedData.map((row) =>
    String(row.dept_id).trim(),
  );
  const parentDeptIdField = getFieldHeaderName("parent_dept_id");

  const graph = {}; // 🟢 For cycle detection

  processedData.forEach((row, index) => {
    const dept_id = String(row.dept_id || "").trim();
    let parent_dept_id = row.parent_dept_id;

    if (
      parent_dept_id === undefined ||
      parent_dept_id === null ||
      String(parent_dept_id).trim() === "" ||
      String(parent_dept_id).trim().toLowerCase() === "null"
    ) {
      return; // Top-level or no parent
    }

    parent_dept_id = String(parent_dept_id).trim();

    // 🔵 Self-parent check
    if (dept_id === parent_dept_id) {
      allValidationErrors.push(
        `Row ${
          index + 1
        }: ${parentDeptIdField} cannot be the same as Department ID (${dept_id}). A department cannot be its own parent.`,
      );
      return;
    }

    // 🔵 Parent exists in file or DB
    if (
      !allDeptIdsInFile.includes(parent_dept_id) &&
      !existingDeptIds.includes(parent_dept_id)
    ) {
      allValidationErrors.push(
        `Row ${
          index + 1
        }: ${parentDeptIdField} "${parent_dept_id}" does not correspond to any Department ID in the file or existing in the database.`,
      );
    }

    // 🟢 Build graph for cycle check
    if (!graph[dept_id]) graph[dept_id] = [];
    graph[dept_id].push(parent_dept_id);
  });

  // 🟢 Cycle Detection using DFS
  const visited = new Set();
  const recStack = new Set();

  const hasCycle = (node) => {
    if (!node) return false;
    if (recStack.has(node)) return true;
    if (visited.has(node)) return false;

    visited.add(node);
    recStack.add(node);

    const neighbors = graph[node] || [];
    for (const neighbor of neighbors) {
      if (hasCycle(neighbor)) return true;
    }

    recStack.delete(node);
    return false;
  };

  for (const node of Object.keys(graph)) {
    if (hasCycle(node)) {
      allValidationErrors.push(
        `Cycle detected: Department ID "${node}" is part of a circular hierarchy.`,
      );
      break;
    }
  }
};

// Main Data Processing Function
const insertOrganogramData = async (jsonData, setValidationDialog) => {
  try {
    // Check company employees
    const { error: usersError, count } = await supabase
      .from("company_user")
      .select("*", {
        count: "exact",
        head: true,
      })
      .eq("company_id", getLocalData("sdfUser")?.company_id)
      .eq("role", "employee");

    if (usersError) {
      console.error("Users Error:", usersError);
      throw new Error("Error fetching company users: " + usersError.message);
    }

    const hasEmployees = count > 0;
    let validEmployeeIds = [];

    if (hasEmployees) {
      const { data: employeeData, error: empError } =
        await fromWithCompany("company_user").select("emp_id");

      if (empError) {
        throw new Error("Error fetching employee IDs: " + empError.message);
      }
      validEmployeeIds = employeeData.map((user) => user.emp_id);
    }

    // Fetch existing departments
    const { data: existingDepts, error: deptError } = await fromWithCompany(
      "organograms",
    )
      .select("dept_id")
      .eq("status", statuses.ACTIVE);

    if (deptError) {
      throw new Error(
        "Error fetching existing departments: " + deptError.message,
      );
    }

    const existingDeptIds = existingDepts.map((dept) => dept.dept_id);
    const fileDeptIds = new Set();
    const allValidationErrors = [];

    // Process and validate data
    const processedData = jsonData.map((row, index) => {
      const validatedDepartments = Object.keys(row).reduce((acc, key) => {
        acc[key] =
          row[key] != null && row[key] !== "" ? String(row[key]).trim() : null;
        return acc;
      }, {});

      const deptNameField = getFieldHeaderName("dept_name");
      const deptIdField = getFieldHeaderName("dept_id");
      const parentDeptIdField = getFieldHeaderName("parent_dept_id");
      const deptHeadIdField = getFieldHeaderName("dept_head_id");

      validateDepartmentData(
        validatedDepartments,
        index,
        allValidationErrors,
        fileDeptIds,
      );

      const rawDeptId = String(validatedDepartments[deptIdField] || "").trim();
      const rawDeptName = String(
        validatedDepartments[deptNameField] || "",
      ).trim();
      const isNullDepartmentRow = !rawDeptId && !rawDeptName;

      const deptId = isNullDepartmentRow
        ? "none"
        : rawDeptId || `DEPT${index + 1}`;
      const deptName = isNullDepartmentRow
        ? "No department"
        : validatedDepartments[deptNameField] || null;
      const parentDeptId = isNullDepartmentRow
        ? null
        : validatedDepartments[parentDeptIdField] || null;

      if (existingDeptIds.includes(deptId)) {
        allValidationErrors.push(
          `Row ${index + 1}: ${deptIdField} "${deptId}" already exists.`,
        );
      }

      const deptHeadId = validatedDepartments[deptHeadIdField];
      validateDepartmentHead(
        deptHeadId,
        hasEmployees,
        validEmployeeIds,
        index,
        deptHeadIdField,
        allValidationErrors,
      );

      return {
        dept_id: deptId,
        dept_name: deptName,
        parent_dept_id: parentDeptId,
        dept_head_id: deptHeadId || null,
        company_id: getLocalData("sdfUser")?.company_id || null,
        created_by: getLocalData("sdfUser")?.id || null,
        status: "active",
      };
    });

    // Validate parent departments
    validateParentDepartment(
      processedData,
      existingDeptIds,
      allValidationErrors,
    );

    if (allValidationErrors.length > 0) {
      setValidationDialog({
        show: true,
        errors: allValidationErrors,
      });
      return false;
    }

    // Do not save "none" as department in DB – skip rows with no department
    const toInsert = processedData.filter(
      (row) => !isNoDepartment(row.dept_id),
    );

    // Insert data into database
    const { error } = await supabase.from("organograms").insert(toInsert);

    if (error) {
      throw new Error(`Database error: ${error.message}`);
    }

    toast.success("Organogram data uploaded successfully");
    return true;
  } catch (error) {
    toast.error(error.message || "Error inserting organogram data");
    return false;
  }
};

// Main Component
function OrganogramList() {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const [validationDialog, setValidationDialog] = useState({
    show: false,
    errors: [],
  });
  const [deleteDialog, setDeleteDialog] = useState({ show: false });
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [noneDepartmentCount, setNoneDepartmentCount] = useState(0);

  const isNodeExpanded = (deptId) => {
    const found = expandedNodes.find((n) => n.deptId === deptId);
    return found ? found.expanded : false;
  };

  const toggleNodeExpand = (deptId) => {
    setExpandedNodes((prev) => {
      const idx = prev.findIndex((n) => n.deptId === deptId);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx].expanded = !updated[idx].expanded;
        return updated;
      } else {
        return [...prev, { deptId, expanded: true }];
      }
    });
  };

  const filterTreeByExpanded = (node) => {
    if (!node.children || node.children.length === 0) return node;
    const deptId = node.attributes?.DepartmentId;
    if (deptId && !isNodeExpanded(deptId)) {
      return { ...node, children: [] };
    }
    return { ...node, children: node.children.map(filterTreeByExpanded) };
  };

  const getAllDeptIds = (node) => {
    let ids = [];
    if (node && node.attributes && node.attributes.DepartmentId) {
      ids.push(node.attributes.DepartmentId);
    }
    if (node && node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        ids = ids.concat(getAllDeptIds(child));
      });
    }
    return ids;
  };

  const fetchOrganogramData = async () => {
    try {
      const companyId = getLocalData("sdfUser")?.company_id;
      if (!companyId) {
        if (isMountedRef.current) {
          setTreeData({
            name: { name: "Company", totalEmployeeCount: 0 },
            attributes: { isRoot: true },
            children: [],
          });
        }
        return;
      }

      const { data, error } = await supabase.rpc(
        "get_organogram_with_employee_count",
        { p_company_id: companyId },
      );

      if (!isMountedRef.current) return;

      if (error) {
        console.error("Error fetching organogram data:", error);
        const message = error?.message || String(error);
        if (isMountedRef.current) {
          toast.error("Error fetching organogram data: " + message);
          setTreeData({
            name: { name: "Company", totalEmployeeCount: 0 },
            attributes: { isRoot: true },
            children: [],
          });
        }
        return;
      }

      const rawFlat = Array.isArray(data) ? data : data != null ? [data] : [];
      // Exclude no-department (null, blank, legacy "none") from tree; count is frontend-only
      const flatData = rawFlat.filter(
        (item) => item?.dept_id != null && !isNoDepartment(item.dept_id),
      );
      // Count employees with no department – same as employee list: only count when (role !== SDF) or (SDF with data present)
      const { count } = await supabase
        .from("company_user")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("status", "active")
        .or("department_id.is.null,department_id.eq.,department_id.eq.none")
        .or("data.not.is.null");
      const noneCount = count ?? 0;
      if (isMountedRef.current) setNoneDepartmentCount(noneCount);

      const fetchCompanyName = async () => {
        try {
          const { data, error } = await supabase
            .from("company")
            .select("trading_name")
            .eq("id", getLocalData("sdfUser")?.company_id)
            .single();

          if (error) {
            console.error("Error fetching company name:", error);
            return "Company";
          }
          return data?.trading_name || "Company";
        } catch (err) {
          console.error("Error in fetchCompanyName:", err);
          return "Company";
        }
      };

      let totalEmployeeCount = 0;

      const buildTree = async (flatData) => {
        if (!Array.isArray(flatData) || flatData.length === 0) {
          return {
            name: { name: await fetchCompanyName(), totalEmployeeCount: 0 },
            attributes: { isRoot: true },
            children: [],
          };
        }

        const processedItems = await Promise.all(
          flatData.map(async (item) => {
            totalEmployeeCount += item.employee_count || 0;
            const rawId = item.dept_id;
            const rawName = item.dept_name;
            const rawParent = item.parent_dept_id;
            const isAllNull =
              (rawId == null || String(rawId).trim() === "") &&
              (rawName == null || String(rawName).trim() === "") &&
              (rawParent == null ||
                String(rawParent).trim() === "" ||
                String(rawParent).trim().toLowerCase() === "null");
            const noDept = "None";
            const deptId =
              isAllNull || rawId == null || String(rawId).trim() === ""
                ? noDept
                : String(rawId).trim();
            const deptName =
              isAllNull || rawName == null || String(rawName).trim() === ""
                ? noDept
                : String(rawName).trim();
            const displayName =
              deptId === noDept ? noDept : `${deptId}: ${deptName}`;
            return {
              ...item,
              dept_id: deptId,
              parent_dept_id:
                isAllNull ||
                rawParent == null ||
                String(rawParent).trim() === "" ||
                String(rawParent).trim().toLowerCase() === "null"
                  ? null
                  : String(rawParent).trim(),
              name: displayName,
              attributes: {
                DepartmentId: deptId,
                DepartmentHead: item.dept_head_name || "No Head",
                managerId: item.dept_head_id,
                EmployeeCount:
                  item.total_employee_count ?? item.employee_count ?? 0,
                ApprovalRequired: item.approval_required || false,
              },
              children: [],
            };
          }),
        );

        const idMap = {};
        const processedData = [];
        processedItems.forEach((item) => {
          const key =
            item.dept_id != null && String(item.dept_id).trim() !== ""
              ? item.dept_id
              : "No department";
          if (!idMap[key]) {
            idMap[key] = item;
            processedData.push(item);
          }
        });

        const roots = [];

        processedData.forEach((item) => {
          const parentKey =
            item.parent_dept_id != null &&
            String(item.parent_dept_id).trim() !== "" &&
            String(item.parent_dept_id).trim().toLowerCase() !== "null"
              ? item.parent_dept_id
              : null;
          if (!parentKey) {
            roots.push(item);
          } else {
            const parent = idMap[parentKey];
            if (parent) {
              parent.children = parent.children || [];
              parent.children.push(item);
            } else {
              roots.push(item);
            }
          }
        });

        return {
          name: {
            name: await fetchCompanyName(),
            totalEmployeeCount: totalEmployeeCount,
          },
          attributes: {
            isRoot: true,
          },
          children: roots,
        };
      };

      const treeStructure = await buildTree(flatData);
      if (!isMountedRef.current) return;
      // Include none/blank count in company total
      if (treeStructure?.name && noneCount > 0) {
        treeStructure.name.totalEmployeeCount =
          (treeStructure.name.totalEmployeeCount || 0) + noneCount;
      }
      setTreeData(treeStructure);
      const allIds = getAllDeptIds(treeStructure);
      setExpandedNodes(allIds.map((deptId) => ({ deptId, expanded: true })));
    } catch (error) {
      console.error("Unexpected error fetching organogram data:", error);
      if (isMountedRef.current) {
        toast.error(
          "Unexpected error fetching organogram data: " +
            (error?.message || String(error)),
        );
        setTreeData({
          name: { name: "Company", totalEmployeeCount: 0 },
          attributes: { isRoot: true },
          children: [],
        });
      }
    }
  };

  // Event Handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: null,
          });
          if (!jsonData || jsonData.length === 0) {
            throw new Error(
              "The Excel file is empty. Please add data to the file.",
            );
          }
          const success = await insertOrganogramData(
            jsonData,
            setValidationDialog,
          );
          if (success) {
            await fetchOrganogramData();
          }
        } catch (error) {
          toast.error(
            error.message ||
              "Error processing file. Please check the file format.",
          );
        }
      };

      reader.onerror = () => {
        toast.error("Error reading file");
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error("Error uploading file");
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleClearAll = () => {
    setDeleteDialog({ show: true });
  };

  const confirmClearAll = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("organograms")
        .update({ status: statuses.ARCHIVED })
        .eq("status", statuses.ACTIVE)
        .eq("company_id", getLocalData()?.company_id)
        .neq("dept_id", "none");
      if (error) {
        toast.error("Error clearing data: " + error.message);
        return;
      }
      toast.success("All organogram data cleared successfully");
      setTreeData([]);
      await fetchOrganogramData();
    } catch (err) {
      toast.error("Error clearing data: " + err.message);
    } finally {
      setLoading(false);
      setDeleteDialog({ show: false });
    }
  };

  const handleSingleDelete = async (departmentId) => {
    if (isNoDepartment(departmentId)) {
      toast.error(
        "No-department (None) cannot be deleted; it is counted from employees with no department.",
      );
      return;
    }
    try {
      const { success, error } = await archiveRecords(
        supabase,
        "organograms",
        "dept_id",
        departmentId,
      );

      if (!success) {
        toast.error("Error deleting record: " + error);
        return;
      }

      toast.success("Record deleted successfully");
      await fetchOrganogramData();
    } catch (err) {
      toast.error("Error deleting record: " + err.message);
    }
  };

  const handleApprovalChange = async (event, departmentId) => {
    try {
      const newValue = event.target.checked;

      const { error } = await supabase
        .from("organograms")
        .update({ approval_required: newValue })
        .eq("dept_id", departmentId)
        .eq("status", statuses.ACTIVE);

      if (error) {
        toast.error("Error updating approval status: " + error.message);
        return;
      }

      setTreeData((prevTreeData) => {
        const newTreeData = JSON.parse(JSON.stringify(prevTreeData)); // Deep clone
        const updateNode = (node) => {
          if (node.attributes?.DepartmentId === departmentId) {
            node.attributes.ApprovalRequired = newValue;
          }
          if (node.children) {
            node.children.forEach(updateNode);
          }
        };
        updateNode(newTreeData);
        return newTreeData;
      });

      toast.success("Approval status updated successfully");
    } catch (err) {
      toast.error("Error updating approval status: " + err.message);
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength - 1) + "…" : text;
  };

  const renderCustomNode = ({ nodeDatum, toggleNode }) => (
    <g
      onClick={(e) => {
        e.stopPropagation();
        const deptId = nodeDatum.attributes?.DepartmentId;
        if (deptId) toggleNodeExpand(deptId);
        toggleNode();
      }}
    >
      <rect
        x={-90}
        y={-30}
        width={180}
        height={60}
        rx={5}
        ry={5}
        fill="white"
        stroke="#aae3ff"
        strokeLinecap="1"
      />

      {/* Company Name for root node, Department Name and Head for others */}
      <text
        dy="-1em"
        x="0"
        textAnchor="middle"
        style={{
          fontSize: "12px",
          fill: "#000",
          stroke: "#008CD1",
          strokeWidth: 0.5,
        }}
      >
        {nodeDatum.attributes.isRoot
          ? truncateText(nodeDatum.name.name, 25) +
            " (" +
            nodeDatum.name.totalEmployeeCount +
            ")"
          : truncateText(nodeDatum.name.split(" (")[0], 25)}
      </text>

      {/* Department Head Name - only show for non-root nodes */}
      {!nodeDatum.attributes.isRoot && (
        <>
          {nodeDatum.attributes.DepartmentHead &&
            nodeDatum.attributes.DepartmentHead !== "No Head" && (
              <text
                dy="0.5em"
                x="0"
                textAnchor="middle"
                style={{
                  fontSize: "11px",
                  fill: "#000",
                  stroke: "#000000",
                  strokeWidth: 0.3,
                }}
              >
                Head{" "}
                {nodeDatum.attributes.managerId
                  ? nodeDatum.attributes.managerId +
                    ": " +
                    truncateText(nodeDatum.attributes.DepartmentHead, 12)
                  : truncateText(nodeDatum.attributes.DepartmentHead, 12)}
              </text>
            )}

          <text
            dy="1.75em"
            x="0"
            textAnchor="middle"
            style={{
              fontSize: "11px",
              fill: "#000",
              stroke: "#000000",
              strokeWidth: 0.3,
            }}
          >
            Employees: {nodeDatum.attributes.EmployeeCount}
          </text>
        </>
      )}

      {/* Checkbox - Only show for department nodes, not for the top company node */}
      {nodeDatum.attributes.DepartmentId && (
        <foreignObject x={-10} y={-53} width={24} height={24}>
          <div
            className="tree-checkbox"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <CCheckbox
              checked={Boolean(nodeDatum.attributes.ApprovalRequired)}
              onChange={(e) => {
                e.stopPropagation();
                handleApprovalChange(e, nodeDatum.attributes.DepartmentId);
              }}
            />
          </div>
        </foreignObject>
      )}
    </g>
  );

  // Effects: fetch organogram when component mounts or company_id becomes available (e.g. after login/rehydration)
  const companyId = getLocalData("sdfUser")?.company_id;
  useEffect(() => {
    isMountedRef.current = true;
    fetchOrganogramData();
    return () => {
      isMountedRef.current = false;
    };
  }, [companyId]);

  // Render
  return (
    <div>
      <h2 className="text-3xl leading-3xl text-blue-500 font-semibold mb-4">
        Organogram
      </h2>
      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-2 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            The organogram should consist of the organizational hierarchy
            clearly stating the department structure like the head department,
            the sub department and their department head IDs. &nbsp;
            <CButton
              className="text-blue-600 underline font-semibold 2xl:!h-auto !h-auto 2xl:!p-0 !p-0"
              onClick={downloadOrganogramTemplate}
            >
              Click here
            </CButton>
            &nbsp; to download the template. Once completed, upload your file to
            proceed.
          </p>

          <p className="font-medium 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            Leave the head department field empty if the department you are
            entering is the head department and if you are adding the sub
            department make sure to mention the head department ID.
          </p>
        </div>
      </div>

      <div className="bg-white border border-solid border-blue-500 rounded-xl overflow-hidden mb-6">
        <div className="flex items-center justify-end p-3">
          <div className="flex gap-2 mt-2 md:mt-0  ">
            <CButton
              tooltip="Tick the box on top of department nodes,if you want to take the recommendation of the department heads for the training need requirements"
              tooltipPosition={"top"}
              className="text-center !p-0 2xl:!h-fit !h-fit 2xl:!w-auto !w-auto mt-3"
            >
              <InfoIcon className="text-blue-500" style={{ fill: "#1976d2" }} />
            </CButton>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={loading}
            />
            <CButton
              className="gradient-btn text-white shadow-none"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              {loading ? "Uploading..." : "Upload File"}
            </CButton>
            <CButton
              onClick={handleClearAll}
              className="bg-blue-75 text-blue-500 hover:bg-blue-100 border border-solid border-blue-300 rounded-lg p-0 mrs-3"
              disabled={loading || !treeData?.children?.length}
            >
              Remove All
            </CButton>
          </div>
        </div>

        <div className="2xl:h-[calc(100vh-404px)] h-[calc(100vh-394px)] w-full">
          {treeData && treeData.children && treeData.children.length > 0 ? (
            <Tree
              data={filterTreeByExpanded(treeData)}
              orientation="vertical"
              pathFunc="step"
              renderCustomNodeElement={renderCustomNode}
              separation={{ siblings: 2, nonSiblings: 2.5 }}
              translate={{ x: window.innerWidth / 2, y: 50 }}
            />
          ) : (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <p className="text-gray-700 text-lg font-semibold mb-4">
                  No data available. Please upload organogram data.
                </p>
                {noneDepartmentCount > 0 && (
                  <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-left">
                    <span className="text-gray-600 font-medium">None:</span>
                    <span className="text-blue-600 font-bold text-xl">
                      {noneDepartmentCount}
                    </span>
                    <span className="text-gray-500 text-sm">
                      employees with no department
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {treeData?.children?.length > 0 && noneDepartmentCount > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-200 text-sm text-gray-700">
            <span className="font-medium">None:</span>{" "}
            <span className="font-semibold text-blue-600">
              {noneDepartmentCount}
            </span>
            <span className="text-gray-500 ml-1">
              (employees with no department)
            </span>
          </div>
        )}
      </div>

      {/* Validation Error Dialog */}
      <CDialog
        open={validationDialog.show}
        onClose={() => setValidationDialog({ show: false, errors: [] })}
        className="validation-dialog bg-[#95AEBB80]"
      >
        <div className="p-5">
          <p className="text-2xl leading-2xl text-grey-700 font-semibold text-center mb-4">
            Validation Errors
          </p>
          <div className="max-h-[400px] overflow-y-auto">
            {validationDialog.errors.map((error, index) => (
              <p key={index} className="text-gray-300 mb-2">
                {error}
              </p>
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <CButton
              onClick={() => setValidationDialog({ show: false, errors: [] })}
              className="bg-blue-500 hover:bg-blue-700 min-w-[80px] text-white border border-solid border-blue-500 hover:border-blue-700"
            >
              Close
            </CButton>
          </div>
        </div>
      </CDialog>

      <ConfirmationDialog
        open={deleteDialog.show}
        onClose={() => setDeleteDialog({ show: false })}
        onConfirm={confirmClearAll}
        title="Clear All Organogram Data"
        description="Are you sure you want to clear all organogram data? This action cannot be undone and will remove all department structure information."
        confirmText="Clear All"
        cancelText="Cancel"
        loading={loading}
        confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
        cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
      />
    </div>
  );
}

export default OrganogramList;
